import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../api/client";

/**
 * Landing page for the verification link.
 *
 * The backend renders its own confirmation page when the token is valid, so
 * this screen exists for everything around that: an expired or already-used
 * link, and requesting a fresh one.
 */
export default function VerifPage() {
  const [params] = useSearchParams();
  const state = params.get("status") || "success";

  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  async function resend(e) {
    e.preventDefault();
    setMsg(null);

    if (!email.trim()) {
      setMsg({ kind: "error", text: "Enter the email address you registered with." });
      return;
    }

    setBusy(true);
    try {
      const res = await api.post("/users/resend-verification", { email: email.trim() });
      setMsg({
        kind: "success",
        text:
          res.data?.message ||
          "If that address has an unverified account, a new link has been sent.",
      });
    } catch (err) {
      setMsg({
        kind: "error",
        text: err.response?.data?.message || "Could not send a new link. Try again shortly.",
      });
    } finally {
      setBusy(false);
    }
  }

  const failed = state !== "success";

  return (
    <div className="es-status-page es-rise">
      <div
        aria-hidden="true"
        style={{
          width: "3.75rem",
          height: "3.75rem",
          margin: "0 auto 1.5rem",
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          background: failed ? "var(--es-warn-bg)" : "var(--es-ok-bg)",
        }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
             stroke={failed ? "var(--es-warn)" : "var(--es-ok)"}
             strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          {failed ? <><path d="M12 9v4" /><path d="M12 17h.01" /><circle cx="12" cy="12" r="9" /></>
                  : <path d="M20 6 9 17l-5-5" />}
        </svg>
      </div>

      <p className="es-status-page__eyebrow">
        {failed ? "Link problem" : "Email verified"}
      </p>

      <h1 className="es-status-page__title">
        {failed ? "That link didn't work" : "Your email is verified"}
      </h1>

      <p className="es-status-page__body">
        {failed
          ? "Verification links expire after a few hours and can only be used once. Request a new one below."
          : "Thanks for confirming your address. Your EduSpecial account is now active and you can sign in."}
      </p>

      {!failed && (
        <div className="es-status-page__actions">
          <Link className="es-btn es-btn--primary" to="/signin">Go to sign in</Link>
          <Link className="es-btn es-btn--ghost" to="/">Back to home</Link>
        </div>
      )}

      {failed && (
        <div className="es-card" style={{ textAlign: "left", marginTop: "0.5rem" }}>
          <div className="es-card__body">
            <h2 className="es-h3">Send me a new link</h2>

            {msg && (
              <div
                className={`es-alert es-alert--${msg.kind === "success" ? "success" : "error"}`}
                role={msg.kind === "success" ? "status" : "alert"}
              >
                {msg.text}
              </div>
            )}

            <form onSubmit={resend} noValidate>
              <div className="es-field">
                <label className="es-label" htmlFor="rv-email">Email address</label>
                <input
                  id="rv-email"
                  type="email"
                  className="es-input"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button type="submit" className="es-btn es-btn--primary" disabled={busy}>
                  {busy ? "Sending…" : "Send new link"}
                </button>
                <Link className="es-btn es-btn--ghost" to="/signin">Back to sign in</Link>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
